import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { Seo } from "../components/Seo";
import { Marquee } from "../components/Marquee";
import { MaskedLines, FadeUp, Eyebrow } from "../components/Reveal";
import { useSettings } from "../lib/SettingsContext";

export const IMG = {
  hero: "https://images.unsplash.com/photo-1769695832228-ab959c7c7e0d?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80",
  boiler: "https://images.unsplash.com/photo-1773517459405-67adee847117?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
  manifold: "https://images.unsplash.com/photo-1707409464203-df2f21c32b9c?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
  filter: "/images/filtro-acqua.jpg",
  technical: "https://images.unsplash.com/photo-1759148414485-5f624fe9d1ea?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
  buildings: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
};

export const SERVICES = [
  {
    id: "boiler",
    name: "Pulizia e decalcificazione boiler",
    desc: "Rimozione dei depositi di calcare, controllo dei componenti accessibili e individuazione di eventuali anomalie, con rapporto finale documentato.",
    price: "da CHF 390",
    cta: "Prenota pulizia boiler",
  },
  {
    id: "serpentine",
    name: "Lavaggio circuiti di riscaldamento a pavimento",
    desc: "Lavaggio e manutenzione dei circuiti di riscaldamento a pavimento, con verifica delle condizioni operative e documentazione del lavoro eseguito.",
    price: "CHF 80 / circuito",
    cta: "Richiedi lavaggio serpentine",
  },
  {
    id: "filtri",
    name: "Manutenzione filtri acqua sanitaria",
    desc: "Sostituzione dell'elemento filtrante standard compatibile, verifica del corretto rimontaggio e controllo finale dell'impianto.",
    price: "CHF 150 · filtro compreso",
    cta: "Prenota manutenzione filtro",
  },
];

const PILLARS = [
  { n: "01", title: "Prezzi chiari", text: "Tariffe finali per gli interventi standard, confermate prima dell'esecuzione. Nessun costo nascosto." },
  { n: "02", title: "Interventi documentati", text: "Ogni manutenzione si conclude con un rapporto del lavoro eseguito e dei controlli effettuati." },
  { n: "03", title: "Manutenzione programmata", text: "Interventi pianificabili nel tempo per privati, condomini e amministrazioni immobiliari." },
  { n: "04", title: "Un unico referente", text: "Un solo interlocutore per la richiesta, l'intervento e le verifiche finali." },
];

const METHOD_STEPS = [
  { n: "1", title: "Richiesta", text: "Ci descrivi l'impianto e il servizio richiesto, allegando se vuoi una fotografia." },
  { n: "2", title: "Verifica dell'impianto", text: "Valutiamo modello, accessibilità e condizioni tecniche dell'impianto." },
  { n: "3", title: "Conferma del prezzo", text: "Confermiamo il costo dell'intervento prima dell'esecuzione, senza sorprese." },
  { n: "4", title: "Intervento", text: "Eseguiamo la manutenzione secondo la procedura prevista per il tuo impianto." },
  { n: "5", title: "Rapporto finale", text: "Documentiamo controlli effettuati, eventuali anomalie e raccomandazioni." },
];

const WHY_POINTS = [
  { t: "Prezzo confermato in anticipo", d: "Il costo dell'intervento standard è definito prima dell'esecuzione." },
  { t: "Rapporto finale", d: "Ogni intervento viene documentato: controlli, anomalie e raccomandazioni." },
  { t: "Identificazione degli impianti", d: "Ogni impianto viene registrato, per manutenzioni successive più rapide." },
  { t: "Radicamento territoriale", d: "Operiamo in Ticino, con partenza dal Mendrisiotto: tempi di intervento contenuti." },
];

export default function HomePage() {
  const { settings } = useSettings();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const serviceImages = { boiler: IMG.boiler, serpentine: IMG.manifold, filtri: IMG.filter };

  return (
    <div data-testid="home-page">
      <Seo
        title="Manutenzione impianti in Ticino | V32 Group — Manutenzioni Tecniche"
        description="Pulizia boiler, lavaggio circuiti di riscaldamento a pavimento e manutenzione filtri acqua sanitaria in Ticino. Interventi professionali, prezzi chiari e documentazione completa."
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden bg-antracite">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img
            src={IMG.hero}
            alt="Locale tecnico moderno con impianto di produzione acqua calda sanitaria"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-antracite via-antracite/55 to-antracite/20" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pb-20 md:pb-28 pt-40">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-bronzo mb-6"
            data-testid="hero-territory-label"
          >
            Mendrisiotto · Ticino
          </motion.p>
          <MaskedLines
            lines={["Manutenzione professionale", "degli impianti."]}
            className="font-display font-light text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.06] text-avorio max-w-3xl"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-avorio/75 text-base md:text-lg leading-relaxed max-w-xl"
          >
            Pulizia boiler, lavaggio circuiti di riscaldamento e manutenzione filtri
            acqua sanitaria. Interventi professionali, prezzi chiari e documentazione
            completa.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/contatti"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 bg-bronzo text-white font-medium px-8 py-4 hover:bg-avorio hover:text-antracite transition-colors duration-300"
            >
              Richiedi un intervento <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/prezzi"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 border border-avorio/40 text-avorio font-medium px-8 py-4 hover:border-bronzo hover:text-bronzo transition-colors duration-300"
            >
              Scopri i prezzi
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* PROPOSTA DI VALORE */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <FadeUp>
                <Eyebrow>V32 Manutenzioni Tecniche</Eyebrow>
                <h2 className="mt-6 font-display font-normal text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-ink text-balance">
                  Tre servizi specializzati. Un unico referente.
                </h2>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="mt-6 text-ink/70 leading-relaxed">
                  V32 Group è specializzata nella manutenzione di boiler, circuiti di
                  riscaldamento a pavimento e filtri principali dell'acqua sanitaria.
                  Servizi dedicati a privati e amministrazioni immobiliari in Ticino,
                  con prezzi definiti e rapporti documentati.
                </p>
              </FadeUp>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
                {PILLARS.map((p, i) => (
                  <FadeUp key={p.n} delay={0.08 * i} className="bg-avorio">
                    <div className="p-8 lg:p-10 h-full group hover:bg-beige transition-colors duration-300" data-testid={`pillar-${p.n}`}>
                      <p className="font-mono text-xs text-bronzo tracking-[0.25em]">{p.n}</p>
                      <h3 className="mt-4 font-display font-medium text-xl text-ink group-hover:text-antracite">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm text-ink/60 leading-relaxed">{p.text}</p>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVIZI */}
      <section className="py-20 lg:py-32 bg-beige">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <FadeUp>
            <Eyebrow>I servizi</Eyebrow>
            <h2 className="mt-6 font-display font-normal text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-ink text-balance max-w-2xl">
              Manutenzioni ordinarie, eseguite con metodo.
            </h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.id} delay={0.08 * i}>
                <article className="bg-avorio border border-ink/10 h-full flex flex-col group" data-testid={`service-card-${s.id}`}>
                  <Link to={`/servizi#${s.id}`} className={`img-frame aspect-[4/3] block ${s.id === "filtri" ? "bg-white" : ""}`}>
                    <img src={serviceImages[s.id]} alt={s.name} className={`w-full h-full ${s.id === "filtri" ? "object-contain p-8" : "object-cover"}`} loading="lazy" />
                  </Link>
                  <div className="p-8 flex flex-col flex-1">
                    <p className="font-mono text-xs text-bronzo tracking-[0.25em]">0{i + 1}</p>
                    <h3 className="mt-3 font-display font-medium text-xl text-ink leading-snug">
                      <Link to={`/servizi#${s.id}`} className="hover:text-bronzo transition-colors duration-300">
                        {s.name}
                      </Link>
                    </h3>
                    <p className="mt-3 text-sm text-ink/60 leading-relaxed flex-1">{s.desc}</p>
                    <p className="mt-5 font-display font-medium text-lg text-antracite" data-testid={`service-price-${s.id}`}>
                      {s.price}
                    </p>
                    <div className="mt-6 flex flex-col gap-3">
                      <Link
                        to={`/contatti?servizio=${s.id}`}
                        data-testid={`service-cta-${s.id}`}
                        className="inline-flex items-center justify-center gap-2 bg-antracite text-avorio text-sm font-medium px-5 py-3 hover:bg-bronzo transition-colors duration-300"
                      >
                        {s.cta} <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/servizi#${s.id}`}
                        data-testid={`service-detail-${s.id}`}
                        className="text-center text-sm font-medium text-ink/70 hover:text-bronzo transition-colors duration-300"
                      >
                        Dettagli del servizio
                      </Link>
                    </div>
                  </div>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* METODO */}
      <section className="py-20 lg:py-32 bg-antracite text-avorio">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <FadeUp>
            <Eyebrow dark>Come funziona</Eyebrow>
            <h2 className="mt-6 font-display font-normal text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-balance max-w-2xl">
              Ogni intervento organizzato. Ogni lavoro documentato.
            </h2>
          </FadeUp>

          <div className="mt-16 hidden md:block">
            <div className="relative">
              <div className="absolute top-[7px] left-0 right-0 h-px bg-avorio/15" aria-hidden="true" />
              <div className="grid grid-cols-5 gap-8">
                {METHOD_STEPS.map((s, i) => (
                  <FadeUp key={s.n} delay={0.1 * i}>
                    <div className="relative" data-testid={`method-step-${s.n}`}>
                      <span className="block w-[15px] h-[15px] rotate-45 bg-antracite border border-bronzo mb-8" aria-hidden="true" />
                      <p className="font-mono text-xs text-bronzo tracking-[0.25em]">0{s.n}</p>
                      <h3 className="mt-3 font-display font-medium text-lg">{s.title}</h3>
                      <p className="mt-3 text-sm text-avorio/60 leading-relaxed">{s.text}</p>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12 md:hidden">
            <div className="relative pl-8">
              <div className="absolute top-0 bottom-0 left-[7px] w-px bg-avorio/15" aria-hidden="true" />
              {METHOD_STEPS.map((s, i) => (
                <FadeUp key={s.n} delay={0.06 * i}>
                  <div className="relative pb-10" data-testid={`method-step-mobile-${s.n}`}>
                    <span className="absolute -left-8 top-1 w-[15px] h-[15px] rotate-45 bg-antracite border border-bronzo" aria-hidden="true" />
                    <p className="font-mono text-xs text-bronzo tracking-[0.25em]">0{s.n}</p>
                    <h3 className="mt-2 font-display font-medium text-lg">{s.title}</h3>
                    <p className="mt-2 text-sm text-avorio/60 leading-relaxed">{s.text}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AMMINISTRAZIONI */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <FadeUp className="lg:col-span-6 order-2 lg:order-1">
              <div className="img-frame aspect-[4/5] max-h-[640px] w-full">
                <img
                  src={IMG.buildings}
                  alt="Stabile residenziale moderno gestito da un'amministrazione immobiliare"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </FadeUp>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <FadeUp>
                <Eyebrow>Per le amministrazioni</Eyebrow>
                <h2 className="mt-6 font-display font-normal text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-ink text-balance">
                  Manutenzioni programmate per amministrazioni immobiliari.
                </h2>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="mt-6 text-ink/70 leading-relaxed">
                  Supportiamo le amministrazioni immobiliari nella gestione delle
                  manutenzioni tecniche programmate: interventi organizzati su più
                  unità, prezzi trasparenti e rapporti documentati per ogni impianto.
                </p>
                <Link
                  to="/amministrazioni"
                  data-testid="amministrazioni-link"
                  className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-antracite border-b border-bronzo pb-1 hover:text-bronzo transition-colors duration-300"
                >
                  Scopri il servizio dedicato <ArrowRight className="w-4 h-4" />
                </Link>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* PERCHÉ V32 */}
      <section className="py-20 lg:py-32 bg-beige">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <FadeUp>
                <Eyebrow>Perché V32</Eyebrow>
                <h2 className="mt-6 font-display font-normal text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-ink text-balance">
                  L'organizzazione è parte della qualità.
                </h2>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="mt-6 text-ink/70 leading-relaxed">
                  Una manutenzione ben fatta si riconosce da come viene gestita:
                  prezzo definito prima dell'intervento, lavoro documentato e un
                  unico referente che conosce il tuo impianto.
                </p>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {WHY_POINTS.map((item) => (
                    <div key={item.t} className="border-l-2 border-bronzo/50 pl-5" data-testid={`why-${item.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <p className="font-display font-medium text-base text-ink">{item.t}</p>
                      <p className="mt-2 text-sm text-ink/60 leading-relaxed">{item.d}</p>
                    </div>
                  ))}
                </div>
              </FadeUp>
            </div>
            <FadeUp className="lg:col-span-6" delay={0.1}>
              <div className="img-frame aspect-[4/5] max-h-[640px] w-full">
                <img
                  src={IMG.technical}
                  alt="Dettaglio tecnico di tubazioni e valvole di un impianto idraulico"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="bg-antracite text-avorio py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
          <FadeUp>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-bronzo mb-6">
              V32 Manutenzioni Tecniche · Mendrisiotto, Ticino
            </p>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-balance max-w-3xl mx-auto">
              Devi pianificare una manutenzione?
            </h2>
            <p className="mt-6 text-avorio/70 leading-relaxed max-w-xl mx-auto">
              Descrivici l'impianto e il servizio richiesto: confermeremo prezzo e
              disponibilità prima dell'intervento.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contatti"
                data-testid="final-cta-button"
                className="inline-flex items-center gap-2 bg-bronzo text-white font-medium px-8 py-4 hover:bg-avorio hover:text-antracite transition-colors duration-300"
              >
                Richiedi un intervento <ArrowRight className="w-4 h-4" />
              </Link>
              {settings.phone && (
                <a
                  href={`tel:${settings.phone.replace(/\s/g, "")}`}
                  data-testid="final-cta-phone"
                  className="inline-flex items-center gap-2 border border-avorio/30 text-avorio font-medium px-8 py-4 hover:border-bronzo hover:text-bronzo transition-colors duration-300"
                >
                  <Phone className="w-4 h-4" /> {settings.phone}
                </a>
              )}
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
